import { Check } from "lucide-react";

/* 3-step pill breadcrumb for the public "Send a case" form.
 *
 * Only rendered for anonymous submitters — authenticated clients skip the
 * verify step, so showing a breadcrumb there would be misleading.
 *
 * step semantics (passed in by the parent):
 *   1 — Details        · form is open, no code yet
 *   2 — Verify         · code has been emailed, awaiting 6-digit entry
 *   3 — Submitted      · case row created server-side
 *
 * Visual language matches the dark Send-a-Case portal:
 *   - Completed steps  → cyan circle + checkmark, cyan label
 *   - Active step      → cyan ring + pulse, white label
 *   - Upcoming steps   → muted slate
 */
const STEPS = [
    { n: 1, label: "Details" },
    { n: 2, label: "Verify" },
    { n: 3, label: "Submitted" },
];

export function WorkflowStepIndicator({ step }) {
    return (
        <div
            className="mt-6 flex items-center gap-1.5 sm:gap-2.5 select-none"
            data-testid="workflow-step-indicator"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={3}
            aria-valuenow={step}
            aria-label={`Step ${step} of 3`}
        >
            {STEPS.map((s, idx) => {
                const isComplete = step > s.n;
                const isActive = step === s.n;
                const isLast = idx === STEPS.length - 1;
                return (
                    <div key={s.n} className="flex items-center gap-1.5 sm:gap-2.5" data-testid={`workflow-step-${s.n}`} data-state={isComplete ? "complete" : isActive ? "active" : "upcoming"}>
                        <div className="flex items-center gap-2">
                            <span
                                className={`relative grid place-items-center h-6 w-6 sm:h-7 sm:w-7 rounded-full font-mono-tr text-[10.5px] sm:text-[11px] transition ${
                                    isComplete
                                        ? "bg-cyan-400 text-[#022c33] ring-1 ring-cyan-300/60"
                                        : isActive
                                            ? "bg-white/10 text-white ring-2 ring-cyan-400"
                                            : "bg-white/5 text-white/40 ring-1 ring-white/10"
                                }`}
                            >
                                {isComplete ? <Check size={13} strokeWidth={3} /> : s.n}
                                {isActive && (
                                    <span className="absolute inset-0 rounded-full ring-2 ring-cyan-400/50 animate-ping" aria-hidden />
                                )}
                            </span>
                            <span
                                className={`text-[11.5px] sm:text-[12.5px] tracking-wide transition ${
                                    isComplete ? "text-cyan-200/90" : isActive ? "text-white font-medium" : "text-white/40"
                                }`}
                            >
                                {s.label}
                            </span>
                        </div>
                        {!isLast && (
                            <span
                                className={`hidden sm:block h-px w-8 lg:w-12 transition-colors ${
                                    isComplete ? "bg-cyan-400/70" : "bg-white/15"
                                }`}
                                aria-hidden
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
